import type { FC, ReactNode } from 'react'
import type { MediaItem, Stat } from '@/types'

import { useRef } from 'react'
import { cn } from '@/lib/utils'
import { MediaFrame } from '@/components/generals/media-frame'
import { StatValue } from '@/components/generals/stat-value'

import {
    FULL_MOTION,
    gsap,
    SplitText,
    useGSAP
} from '@/lib/gsap'

interface IntroImages {
    primary: MediaItem
    primaryCaption: string
    secondary?: MediaItem
    secondaryCaption?: string
}

interface PageIntroProps {
    id: string
    index: string
    label: string
    aside?: string
    /** The large opening statement. Wrap words in <em> for the serif accent. */
    statement: ReactNode
    body?: string
    stats?: Stat[]
    images?: IntroImages
    children?: ReactNode
}

/**
 * The first chapter under every page hero. It slides up over the pinned
 * hero like a sheet, and its statement fills in word by word as you scroll.
 */
export const PageIntro: FC<PageIntroProps> = ({
    id,
    index,
    label,
    aside,
    statement,
    body,
    stats,
    images,
    children
}): ReactNode => {
    const sectionRef = useRef<HTMLElement>(null)

    useGSAP(() => {
        const text = sectionRef.current?.querySelector<HTMLElement>('[data-intro-statement]')
        if (!text) return

        const mm = gsap.matchMedia()

        mm.add(FULL_MOTION, () => {
            const split = SplitText.create(text, {
                type: 'words',
                autoSplit: true,
                onSplit: (self) => {
                    return gsap.fromTo(self.words, { opacity: 0.14 }, {
                        opacity: 1,
                        ease: 'none',
                        stagger: 0.12,
                        scrollTrigger: {
                            trigger: text,
                            start: 'top 82%',
                            end: 'bottom 50%',
                            scrub: true
                        }
                    })
                }
            })

            return () => split.revert()
        })

        return () => mm.revert()
    }, { scope: sectionRef })

    const statList = stats && stats.length > 0 && (
        <dl className={cn('grid grid-cols-2 gap-x-6 gap-y-10', !images && 'lg:grid-cols-4')}>
            {stats.map((stat) => (
                <div key={stat.label} data-reveal className='flex flex-col-reverse border-t border-charcoal/15 pt-4'>
                    <dt className='mt-2 text-sm leading-snug text-charcoal/65'>
                        {stat.label}
                    </dt>

                    <dd className='type-wide text-[clamp(1.9rem,3.2vw,3rem)] font-light leading-none tracking-[-0.03em]'>
                        <StatValue stat={stat} />
                    </dd>
                </div>
            ))}
        </dl>
    )

    return (
        <section
            ref={sectionRef}
            id={id}
            aria-labelledby={`${id}-label`}
            className='relative z-10 -mt-px overflow-hidden rounded-t-[1.75rem] bg-bone text-charcoal shadow-[0_-30px_70px_-20px_rgba(9,17,12,0.55)] sm:rounded-t-[2.5rem]'
        >
            <div className='px-5 pb-24 pt-20 sm:px-8 lg:px-12 lg:pb-32 lg:pt-28'>
                <div className='flex flex-wrap items-center justify-between gap-4'>
                    <h2 id={`${id}-label`} className='flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.24em] text-charcoal/70'>
                        <span className='tabular-nums'>
                            {index}
                        </span>

                        <span aria-hidden='true' className='h-px w-8 bg-current opacity-40' />

                        <span>
                            {label}
                        </span>
                    </h2>

                    {aside && (
                        <p className='font-mono text-[11px] uppercase tracking-[0.24em] text-muted-foreground'>
                            {aside}
                        </p>
                    )}
                </div>

                <p
                    data-intro-statement
                    className='mt-12 max-w-[68rem] text-[clamp(1.6rem,3.5vw,3.4rem)] font-light leading-[1.14] tracking-[-0.02em] lg:mt-16 [&_em]:font-serif [&_em]:font-normal'
                >
                    {statement}
                </p>

                {images ? (
                    <div className='mt-16 grid gap-10 lg:mt-24 lg:grid-cols-12 lg:gap-8'>
                        <div className='flex flex-col justify-between gap-12 lg:col-span-4 lg:py-4'>
                            {body && (
                                <p data-reveal className='max-w-md text-[15px] leading-relaxed text-charcoal/75'>
                                    {body}
                                </p>
                            )}

                            {statList}
                        </div>

                        <div className='relative lg:col-span-8'>
                            <figure>
                                <MediaFrame
                                    image={images.primary}
                                    sizes='(min-width: 1024px) 66vw, 100vw'
                                    mask='left'
                                    parallax={8}
                                    className='aspect-[4/3] rounded-[4px] sm:aspect-[16/10]'
                                />

                                <figcaption className='mt-3 font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground'>
                                    {images.primaryCaption}
                                </figcaption>
                            </figure>

                            {images.secondary && (
                                <figure className='ml-auto mt-8 w-2/3 sm:w-1/2 lg:absolute lg:-bottom-24 lg:right-8 lg:mt-0 lg:w-[32%]'>
                                    <MediaFrame
                                        image={images.secondary}
                                        sizes='(min-width: 1024px) 26vw, 60vw'
                                        mask='up'
                                        parallax={6}
                                        className='aspect-[4/5] rounded-[4px] ring-8 ring-bone'
                                    />

                                    {images.secondaryCaption && (
                                        <figcaption className='mt-3 font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground'>
                                            {images.secondaryCaption}
                                        </figcaption>
                                    )}
                                </figure>
                            )}
                        </div>
                    </div>
                ) : (
                    <div className='mt-16 grid gap-12 lg:mt-20 lg:grid-cols-12 lg:gap-8'>
                        {body && (
                            <p data-reveal className='max-w-md text-[15px] leading-relaxed text-charcoal/75 lg:col-span-4'>
                                {body}
                            </p>
                        )}

                        <div className={cn(body ? 'lg:col-span-8' : 'lg:col-span-12')}>
                            {statList}
                        </div>
                    </div>
                )}

                {children}
            </div>
        </section>
    )
}
